import type { Rounding } from "./schema";
import { MS_PER_MINUTE, MS_PER_SECOND, TIME_ZONE, type PlainDate } from "./types";

export function roundTo(ms: number, unitMs: number, rounding: Rounding): number {
  const units = ms / unitMs;
  return (rounding === "floor" ? Math.floor(units) : Math.ceil(units)) * unitMs;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(seconds: boolean): Intl.DateTimeFormat {
  const key = seconds ? "s" : "m";
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", {
      timeZone: TIME_ZONE,
      hour: "2-digit",
      minute: "2-digit",
      second: seconds ? "2-digit" : undefined,
      hourCycle: "h23",
    });
    formatters.set(key, f);
  }
  return f;
}

/**
 * Formats an instant as Israel local time. Rounding is always in the stringent
 * direction of the zman: deadlines round down, earliest-allowed times round up.
 */
export function formatTime(
  ms: number,
  rounding: Rounding,
  options: { seconds?: boolean } = {},
): string {
  const seconds = options.seconds ?? false;
  const rounded = roundTo(ms, seconds ? MS_PER_SECOND : MS_PER_MINUTE, rounding);
  return formatter(seconds).format(rounded);
}

const dateParts = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The civil date in Israel at the given instant. */
export function israelDateOf(ms: number): PlainDate {
  const parts = Object.fromEntries(dateParts.formatToParts(ms).map((p) => [p.type, p.value]));
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}
